import React from 'react';
import { Icons } from './ui/Icons';

interface AIQuotaDisplayProps {
  used: number;
  limit: number;
  remaining: number;
  className?: string;
}

export const AIQuotaDisplay: React.FC<AIQuotaDisplayProps> = ({
  used,
  limit,
  remaining,
  className = ''
}) => {
  const percentageUsed = (used / limit) * 100;

  // Determine color based on usage
  const getColor = () => {
    if (percentageUsed >= 100) return 'red';
    if (percentageUsed >= 80) return 'orange';
    if (percentageUsed >= 50) return 'yellow';
    return 'green';
  };

  const color = getColor();

  const colorClasses = {
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700',
      icon: 'text-green-600',
      bar: 'bg-green-500'
    },
    yellow: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-700',
      icon: 'text-yellow-600',
      bar: 'bg-yellow-500'
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-700',
      icon: 'text-orange-600',
      bar: 'bg-orange-500'
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      icon: 'text-red-600',
      bar: 'bg-red-500'
    }
  };

  const colors = colorClasses[color];

  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${colors.bg} ${colors.border} ${className}`}>
      <div className="flex items-center gap-2">
        <Icons.Star className={`w-4 h-4 ${colors.icon}`} />
        <span className={`text-xs font-semibold ${colors.text}`}>
          AI Credits
        </span>
      </div>

      <div className="flex items-center gap-2">
        {/* Progress bar */}
        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${colors.bar} transition-all duration-300`}
            style={{ width: `${Math.min(100, percentageUsed)}%` }}
          />
        </div>

        {/* Count */}
        <span className={`text-xs font-mono font-bold ${colors.text} min-w-[45px]`}>
          {remaining}/{limit}
        </span>
      </div>

      {remaining === 0 && (
        <span className="text-xs text-red-600 font-medium">
          Limit reached
        </span>
      )}
    </div>
  );
};
